import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card, Row, Col, Badge, Button, Alert,
  ListGroup, Table, Form, InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit, faArrowLeft, faCheck, faClock,
  faMoneyBillWave, faUser, faCalendar,
  faTag, faExchangeAlt, faFile
} from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { api } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { getIconFromString } from '../../utils/iconUtils';
import FileUpload from './FileUpload';
import PaymentStatusBadge from '../common/PaymentStatusBadge';
import { buildMonzoPaymentLink, normalizeMonzoReference } from '../../utils/monzo';

const formatPounds = (value) => {
  const n = parseFloat(value);
  return (Number.isNaN(n) ? 0 : n).toFixed(2);
};

const ExpenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [reimbursementInfo, setReimbursementInfo] = useState(null);
  const [expenseFiles, setExpenseFiles] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const amountPaid = parseFloat(reimbursementInfo?.amountPaid || 0);
  const remainingAmount = reimbursementInfo?.remainingAmount != null
    ? parseFloat(reimbursementInfo.remainingAmount)
    : parseFloat(reimbursementInfo?.reimbursementAmount || 0);
  const isPartiallyPaid = !expense?.isPaid && amountPaid > 0;
  
  // Fetch expense details
  useEffect(() => {
    const fetchExpenseData = async () => {
      try {
        setLoading(true);
        
        // Get expense details
        const expenseRes = await api.get(`/expenses/${id}`);
        if (!expenseRes.data.success) {
          throw new Error('Failed to load expense details');
        }
        setExpense(expenseRes.data.data);
        
        // Set expense files
        if (expenseRes.data.data.files) {
          setExpenseFiles(expenseRes.data.data.files);
        }
        
        // Get users for display names
        const usersRes = await api.get('/users');
        setUsers(usersRes.data.data);
        
        // Use reimbursement data from main expense response
        if (expenseRes.data.data && expenseRes.data.data.reimbursement) {
          const reimbursement = expenseRes.data.data.reimbursement;
          setReimbursementInfo(reimbursement);
          const remaining = reimbursement.remainingAmount != null
            ? reimbursement.remainingAmount
            : reimbursement.reimbursementAmount;
          setPaymentAmount(formatPounds(remaining));
        } else {
          setReimbursementInfo(null);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching expense data:', err);
        setError(err.message || 'Failed to load expense data');
        setLoading(false);
      }
    };
    
    fetchExpenseData();
  }, [id]);
  
  // Helper to find user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.displayName : 'Unknown User';
  };
  
  // Helper to get monzo.me URL, including the expense title as the payment reference
  const getMonzoUrl = (userId, amount) => {
    const user = users.find(u => u.id === userId);
    if (!user || !user.monzoUsername) return null;
    return buildMonzoPaymentLink(user.monzoUsername, amount, expense?.title);
  };

  const parsedPaymentAmount = parseFloat(paymentAmount);
  const paymentAmountIsPayable =
    Number.isFinite(parsedPaymentAmount) &&
    parsedPaymentAmount >= 0.01 &&
    parsedPaymentAmount <= remainingAmount + 0.001;
  const viewerOwesReclaim = Boolean(
    currentUser && reimbursementInfo?.owedByUserId === currentUser.id
  );
  const partialMonzoUrl = viewerOwesReclaim && paymentAmountIsPayable
    ? getMonzoUrl(expense?.paidByUserId, parsedPaymentAmount)
    : null;
  const monzoReference = normalizeMonzoReference(expense?.title);

  const refreshExpense = async () => {
    const expenseRes = await api.get(`/expenses/${id}`);
    if (!expenseRes.data.success) {
      throw new Error('Failed to refresh expense');
    }
    const data = expenseRes.data.data;
    setExpense(data);
    if (data.files) {
      setExpenseFiles(data.files);
    }
    if (data.reimbursement) {
      setReimbursementInfo(data.reimbursement);
      const remaining = data.reimbursement.remainingAmount != null
        ? data.reimbursement.remainingAmount
        : data.reimbursement.reimbursementAmount;
      setPaymentAmount(formatPounds(remaining));
    }
    setPaymentNote('');
    setPaymentError(null);
    return data;
  };
  
  // Mark expense as paid (settles any remaining reclaim)
  const markAsPaid = async () => {
    try {
      const response = await api.patch(`/expenses/${id}/mark-paid`);
      
      if (response.data.success) {
        await refreshExpense();
      }
    } catch (err) {
      console.error('Error marking expense as paid:', err);
      alert('Failed to mark expense as paid.');
    }
  };

  const recordPayment = async (event) => {
    event.preventDefault();
    setPaymentError(null);
    setPaymentSubmitting(true);
    try {
      const response = await api.post(`/expenses/${id}/payments`, {
        amount: paymentAmount,
        note: paymentNote || undefined,
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to record payment');
      }
      await refreshExpense();
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Failed to record payment';
      setPaymentError(message);
    } finally {
      setPaymentSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading expense details...</p>
      </div>
    );
  }

  if (error || !expense) {
    return (
      <Alert variant="danger" className="my-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error || 'Expense not found'}</p>
        <div className="d-flex justify-content-end">
          <Button 
            variant="outline-danger" 
            onClick={() => navigate('/expenses')}
          >
            Back to Expenses
          </Button>
        </div>
      </Alert>
    );
  }

  // Map backend splitMethod to frontend splitType
  const getSplitType = () => {
    switch (expense.splitMethod) {
      case 'SALARY_PERCENTAGE':
        return 'percentage';
      case 'FIFTY_FIFTY':
        return 'equal';
      case 'FULL_RECLAIM':
        return 'full';
      case 'NO_REIMBURSEMENT':
        return 'none';
      default:
        return null;
    }
  };
  
  // Get the normalized split type
  const splitType = getSplitType();

  // Determine split type text
  const getSplitTypeText = () => {
    switch (splitType) {
      case 'percentage':
        return 'Based on salary percentages';
      case 'equal':
        return 'Split 50/50 between both users';
      case 'full':
        return 'Full reclaim — other person reimburses 100%';
      case 'none':
        return 'No split - personal expense';
      default:
        return 'Unknown split type';
    }
  };

  return (
    <div className="expense-detail">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Expense Details</h1>
        <div>
          <Button
            variant="outline-secondary"
            className="me-2"
            onClick={() => navigate('/expenses')}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Expenses
          </Button>
          <Button
            as={Link}
            to={`/expenses/${id}/edit`}
            variant="primary"
          >
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Expense
          </Button>
        </div>
      </div>
      
      <Row>
        <Col lg={8}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">{expense.title}</h4>
                <h3 className="mb-0">£{(expense.amount != null && !isNaN(expense.amount) ? parseFloat(expense.amount).toFixed(2) : '0.00')}</h3>
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col sm={6} md={3} className="mb-3 mb-md-0">
                  <div className="d-flex align-items-center">
                    <div className="me-2">
                      <FontAwesomeIcon icon={faCalendar} className="text-muted" />
                    </div>
                    <div>
                      <div className="text-muted small">Date</div>
                      <div>
                        <div>{format(new Date(expense.date), 'MMM d, yyyy')}</div>
                        {new Date(expense.date) > new Date() && (
                          <Badge bg="info" size="sm" className="mt-1">
                            <FontAwesomeIcon icon={faCalendar} className="me-1" />
                            Future
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col sm={6} md={3} className="mb-3 mb-md-0">
                  <div className="d-flex align-items-center">
                    <div className="me-2">
                      <FontAwesomeIcon icon={faTag} className="text-muted" />
                    </div>
                    <div>
                      <div className="text-muted small">Category</div>
                      <div>
                        {expense.Category ? (
                          <Badge 
                            pill 
                            style={{ 
                              backgroundColor: expense.Category.color || '#6c757d',
                              color: '#fff' 
                            }}
                          >
                            {expense.Category.icon && (
                              <FontAwesomeIcon 
                                icon={getIconFromString(expense.Category.icon, faTag)} 
                                className="me-1" 
                              />
                            )}
                            {expense.Category.name}
                          </Badge>
                        ) : (
                          <Badge pill bg="secondary">Uncategorized</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col sm={6} md={3}>
                  <div className="d-flex align-items-center">
                    <div className="me-2">
                      <FontAwesomeIcon icon={faUser} className="text-muted" />
                    </div>
                    <div>
                      <div className="text-muted small">Paid By</div>
                      <div>
                        <Badge bg="primary">
                          {getUserName(expense.paidByUserId)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col sm={6} md={3}>
                  <div className="d-flex align-items-center">
                    <div className="me-2">
                      {expense.isPaid ? (
                        <FontAwesomeIcon icon={faCheck} className="text-success" />
                      ) : (
                        <FontAwesomeIcon icon={faClock} className="text-warning" />
                      )}
                    </div>
                    <div>
                      <div className="text-muted small">Status</div>
                      <div>
                        {expense.isPaid ? (
                          <PaymentStatusBadge expense={expense} />
                        ) : isPartiallyPaid ? (
                          <PaymentStatusBadge expense={expense} showRemaining />
                        ) : (
                          <PaymentStatusBadge expense={expense} />
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
              
              {expense.description && (
                <div className="mb-4">
                  <h5>Description</h5>
                  <p className="mb-0">{expense.description}</p>
                </div>
              )}
              
              {/* Show labels if present */}
              {(expense.Labels && expense.Labels.length > 0) && (
                <div className="mb-4">
                  <h5>Labels</h5>
                  <div>
                    {expense.Labels.map(label => (
                      <span key={label.id} style={{ display: 'inline-block', marginRight: '0.5rem', marginBottom: '0.25rem', verticalAlign: 'middle' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            backgroundColor: label.color || '#6c757d',
                            marginRight: '6px',
                            verticalAlign: 'middle',
                            border: '1px solid #ccc'
                          }}
                        ></span>
                        <span style={{ verticalAlign: 'middle' }}>{label.name}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mb-4">
                <h5 className="mb-3">Expense Details</h5>
                <ListGroup variant="flush" className="border rounded">
                  <ListGroup.Item className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faUser} className="text-primary me-2" />
                      Paid By
                    </div>
                    <div>
                      <strong>{getUserName(expense.paidByUserId)}</strong>
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faExchangeAlt} className="text-primary me-2" />
                      Split Type
                    </div>
                    <div>
                      {splitType === 'percentage' && (
                        <Badge bg="primary">Salary %</Badge>
                      )}
                      {splitType === 'equal' && (
                        <Badge bg="info">50/50</Badge>
                      )}
                      {splitType === 'none' && (
                        <Badge bg="secondary">No Split</Badge>
                      )}
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <div className="text-muted small">{getSplitTypeText()}</div>
                  </ListGroup.Item>
                  
                  {expense.isPaid && (
                    <>
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faUser} className="text-success me-2" />
                          Settled
                        </div>
                        <div>
                          {expense.payments?.length
                            ? getUserName(expense.payments[expense.payments.length - 1].recordedByUserId)
                            : 'Confirmed'}
                        </div>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <div className="text-muted small">
                          Marked as paid on {format(new Date(expense.updatedAt), 'MMM d, yyyy')}
                        </div>
                      </ListGroup.Item>
                    </>
                  )}
                </ListGroup>
              </div>
            </Card.Body>
          </Card>
          
          {/* Files Section */}
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faFile} className="me-2" />
                Receipts & Documents
              </h5>
            </Card.Header>
            <Card.Body>
              <FileUpload 
                expenseId={expense.id}
                existingFiles={expenseFiles}
                onFilesUploaded={setExpenseFiles}
              />
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          {/* Reimbursement Card */}
          {splitType !== 'none' && reimbursementInfo && (
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                  Reimbursement Details
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <strong>Initially paid by:</strong> {getUserName(expense.paidByUserId)}
                </div>
                
                <div className="mb-3">
                  <strong>Split Calculation:</strong> {reimbursementInfo.splitExplanation || `Split ${splitType === 'equal' ? '50/50' : splitType === 'percentage' ? 'by salary %' : 'unknown'}`}
                </div>

                {expense.isPaid && (
                  <Alert variant="success">
                    <Alert.Heading>Expense Fully Settled</Alert.Heading>
                    <p className="mb-0">
                      This reclaim is fully reimbursed
                      {expense.payments?.length
                        ? ` (£${formatPounds(
                            expense.payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
                          )} across ${expense.payments.length} contribution${expense.payments.length === 1 ? '' : 's'})`
                        : ''}
                      .
                    </p>
                  </Alert>
                )}
                
                <h6 className="mb-3">Split Calculation:</h6>
                <Table className="mb-4">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Share Amount</th>
                      <th>Share %</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reimbursementInfo.shares && reimbursementInfo.shares.map(share => {
                      const isPayer = share.userId === expense.paidByUserId;
                      const isCurrentUser = share.userId === currentUser.id;
                          
                          return (
                            <tr key={share.userId} className={isPayer ? 'table-primary' : ''}>
                              <td>
                                {getUserName(share.userId)}
                                {isPayer && ' (Payer)'}
                                {isCurrentUser && ' (You)'}
                              </td>
                              <td>£{(share.amount != null && !isNaN(share.amount) ? parseFloat(share.amount).toFixed(2) : '0.00')}</td>
                              <td>{share.percentage.toFixed(1)}%</td>
                              <td>
                                {isPayer ? (
                                  <Badge bg="success">Paid</Badge>
                                ) : (
                                  expense.isPaid ? (
                                    <Badge bg="success">Reimbursed</Badge>
                                  ) : isPartiallyPaid ? (
                                    <Badge bg="info">Partially paid</Badge>
                                  ) : (
                                    <Badge bg="warning">Owes Payment</Badge>
                                  )
                                )}
                              </td>
                            </tr>
                          );
                    })}
                  </tbody>
                </Table>
                
                {splitType === 'percentage' && (
                  <Alert variant="info" className="mb-3">
                    <small>
                      <strong>Salary Split Explanation:</strong> This expense is split based on the salary percentages 
                      for {format(new Date(expense.date), 'MMMM yyyy')}. The split percentages shown reflect each user's 
                      portion of the total household income for that month.
                    </small>
                  </Alert>
                )}
                
                {/* If current user is the one who owes reimbursement, show payment info */}
                {!expense.isPaid && reimbursementInfo && reimbursementInfo.owedByUserId === currentUser.id && (
                      <div className="mb-4">
                        <Alert variant="warning">
                          <Alert.Heading>
                            You owe money for this expense
                          </Alert.Heading>
                          <p className="mb-1">
                            {reimbursementInfo.splitExplanation || `You owe £${formatPounds(reimbursementInfo.reimbursementAmount)} to ${getUserName(expense.paidByUserId)} for this expense.`}
                          </p>
                          <p className="mb-0">
                            Paid so far: £{formatPounds(amountPaid)} · Remaining: £{formatPounds(remainingAmount)}
                          </p>
                        </Alert>
                      </div>
                    )}
                    
                    {/* If current user is the one who paid and is owed money, show that info */}
                    {!expense.isPaid && reimbursementInfo && expense.paidByUserId === currentUser.id && reimbursementInfo.needsReimbursement && (
                      <div className="mb-4">
                        <Alert variant="info">
                          <Alert.Heading>
                            {getUserName(reimbursementInfo.owedByUserId)} owes you money
                          </Alert.Heading>
                          <p className="mb-1">
                            {reimbursementInfo.splitExplanation || `${getUserName(reimbursementInfo.owedByUserId)} owes you £${formatPounds(reimbursementInfo.reimbursementAmount)} for this expense.`}
                          </p>
                          <p className="mb-0">
                            Received: £{formatPounds(amountPaid)} · Remaining: £{formatPounds(remainingAmount)}
                          </p>
                        </Alert>
                      </div>
                    )}

                    {!expense.isPaid && reimbursementInfo?.needsReimbursement && remainingAmount > 0 && (
                      <Card className="border mb-4">
                        <Card.Body>
                          <h6 className="mb-3">Record a contribution</h6>
                          {paymentError && (
                            <Alert variant="danger" className="py-2">
                              {paymentError}
                            </Alert>
                          )}
                          <Form onSubmit={recordPayment}>
                            <Form.Group className="mb-3" controlId="paymentAmount">
                              <Form.Label>Amount</Form.Label>
                              <InputGroup>
                                <InputGroup.Text>£</InputGroup.Text>
                                <Form.Control
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  max={remainingAmount}
                                  value={paymentAmount}
                                  onChange={(e) => setPaymentAmount(e.target.value)}
                                  required
                                />
                              </InputGroup>
                              <Form.Text muted>
                                Remaining reclaim: £{formatPounds(remainingAmount)}
                              </Form.Text>
                            </Form.Group>
                            {partialMonzoUrl && (
                              <div className="d-grid gap-2 mb-3">
                                <Button
                                  variant="outline-primary"
                                  href={partialMonzoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Pay £{formatPounds(parsedPaymentAmount)} with Monzo
                                </Button>
                                {monzoReference && (
                                  <Form.Text muted>
                                    Reference: {monzoReference}
                                  </Form.Text>
                                )}
                              </div>
                            )}
                            <Form.Group className="mb-3" controlId="paymentNote">
                              <Form.Label>Note (optional)</Form.Label>
                              <Form.Control
                                type="text"
                                maxLength={255}
                                value={paymentNote}
                                onChange={(e) => setPaymentNote(e.target.value)}
                                placeholder="e.g. Monzo transfer"
                              />
                            </Form.Group>
                            <div className="d-grid gap-2">
                              <Button
                                type="submit"
                                variant="primary"
                                disabled={paymentSubmitting}
                              >
                                {paymentSubmitting
                                  ? 'Recording…'
                                  : paymentAmountIsPayable
                                    ? `Record £${formatPounds(parsedPaymentAmount)} contribution`
                                    : 'Record contribution'}
                              </Button>
                              <Button
                                type="button"
                                variant="outline-success"
                                disabled={paymentSubmitting}
                                onClick={markAsPaid}
                              >
                                <FontAwesomeIcon icon={faCheck} className="me-2" />
                                Settle remaining (£{formatPounds(remainingAmount)})
                              </Button>
                            </div>
                          </Form>
                        </Card.Body>
                      </Card>
                    )}

                    {expense.payments && expense.payments.length > 0 && (
                      <div className="mb-4">
                        <h6 className="mb-2">Contributions</h6>
                        <ListGroup variant="flush" className="border rounded">
                          {expense.payments.map((payment) => (
                            <ListGroup.Item
                              key={payment.id}
                              className="d-flex justify-content-between align-items-start"
                            >
                              <div>
                                <div className="fw-medium">£{formatPounds(payment.amount)}</div>
                                <div className="text-muted small">
                                  {getUserName(payment.recordedByUserId)}
                                  {payment.note ? ` · ${payment.note}` : ''}
                                </div>
                              </div>
                              <div className="text-muted small">
                                {format(new Date(payment.createdAt), 'MMM d, yyyy')}
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    )}
                    
                    {/* Fallback for legacy code - should be covered by the above conditions now */}
                    {reimbursementInfo && reimbursementInfo.paymentAction && (
                      <div className="mb-4">
                        <Alert variant={reimbursementInfo.paymentAction.fromUserId === currentUser.id ? "warning" : "info"}>
                          <Alert.Heading>
                            {reimbursementInfo.paymentAction.fromUserId === currentUser.id ? 
                              "You Need to Pay" : 
                              `${getUserName(reimbursementInfo.paymentAction.fromUserId)} Needs to Pay`}
                          </Alert.Heading>
                          <p>{reimbursementInfo.paymentAction.description}</p>
                          
                          {reimbursementInfo.paymentAction.fromUserId === currentUser.id ? (
                            <div className="d-grid gap-2 mt-3">
                              <Button 
                                variant="primary"
                                href={getMonzoUrl(
                                  reimbursementInfo.paymentAction.toUserId, 
                                  reimbursementInfo.paymentAction.amount
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Pay £{(reimbursementInfo.paymentAction.amount != null && !isNaN(reimbursementInfo.paymentAction.amount) ? parseFloat(reimbursementInfo.paymentAction.amount).toFixed(2) : '0.00')} with Monzo
                              </Button>
                            </div>
                          ) : (
                            <p className="mb-0">
                              <strong>Note:</strong> {getUserName(reimbursementInfo.paymentAction.fromUserId)} will need to reimburse you.
                            </p>
                          )}
                        </Alert>
                      </div>
                    )}
              </Card.Body>
            </Card>
          )}
          
          {splitType === 'none' && !expense.isPaid && (
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-light">
                <h5 className="mb-0">Mark as Paid</h5>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="mb-3">
                  This is a personal expense that doesn't require reimbursement.
                </Alert>
                
                <div className="d-grid">
                  <Button 
                    variant="success"
                    onClick={markAsPaid}
                  >
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    Mark as Paid
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
          
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Expense Timeline</h5>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item>
                <div className="d-flex">
                  <div className="me-3">
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', marginTop: '6px' }} className="bg-primary"></div>
                  </div>
                  <div>
                    <div className="fw-medium">Created</div>
                    <div className="text-muted small">
                      {format(new Date(expense.createdAt), 'MMM d, yyyy h:mm a')}
                    </div>
                    <div className="small">
                      By {getUserName(expense.createdByUserId)}
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
              
              {expense.updatedAt !== expense.createdAt && (
                <ListGroup.Item>
                  <div className="timeline-item">
                    <div className="timeline-dot" style={{ backgroundColor: '#2196F3' }}></div>
                    <div className="timeline-content">
                      <div className="timeline-header">Updated</div>
                      <div className="timeline-body">
                        <div className="small">
                          {format(new Date(expense.updatedAt), 'MMM d, yyyy h:mm a')}
                        </div>
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
              )}
              
              {expense.isPaid && (
                <ListGroup.Item>
                  <div className="timeline-item">
                    <div className="timeline-dot" style={{ backgroundColor: '#4CAF50' }}></div>
                    <div className="timeline-content">
                      <div className="timeline-header">Fully reimbursed</div>
                      <div className="timeline-body">
                        <div className="small">
                          {format(new Date(expense.updatedAt), 'MMM d, yyyy h:mm a')}
                        </div>
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
              )}
              {!expense.isPaid && isPartiallyPaid && (
                <ListGroup.Item>
                  <div className="timeline-item">
                    <div className="timeline-dot" style={{ backgroundColor: '#0dcaf0' }}></div>
                    <div className="timeline-content">
                      <div className="timeline-header">Partial contributions recorded</div>
                      <div className="timeline-body">
                        <div className="small">
                          £{formatPounds(amountPaid)} of £{formatPounds(reimbursementInfo?.reimbursementAmount)} paid
                        </div>
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
              )}
            </ListGroup>
          </Card>
        </Col>
      </Row>
      
      {/* Timeline dot styling moved to inline styles */}
    </div>
  );
};

export default ExpenseDetail;
